import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import { getLocalizedText, formatDate } from '../utils/helpers';
import { Badge } from '../components/ui/badge';
import { Calendar, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function KnowledgePage() {
  const { t, i18n } = useTranslation();
  const { articles, fetchArticles } = useAppStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchArticles();
      setLoading(false);
    };
    load();
  }, [fetchArticles]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="knowledge-page">
      <h1 className="font-serif text-4xl font-bold text-primary mb-8">{t('nav.knowledge')}</h1>
      
      {loading ? (
        <div className="text-center py-12"><p className="text-secondary">{t('common.loading')}</p></div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12"><p className="text-secondary">{t('common.noResults')}</p></div>
      ) : (
        <div className="space-y-4">
          {articles.map((article) => (
            <Link to={`/article/${article.id}`} key={article.id} data-testid={`article-card-${article.id}`}>
              <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
                <h2 className="font-serif text-xl font-semibold text-primary mb-2">{getLocalizedText(article.title, i18n.language)}</h2>
                <p className="text-secondary line-clamp-2">{getLocalizedText(article.summary, i18n.language)}</p>
                <div className="flex items-center mt-4 text-sm text-secondary">
                  <Calendar className="h-4 w-4 mr-1" />
                  {formatDate(article.created_at)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ArticleDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/articles/${id}`);
        setArticle(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <div className="text-center py-12">{t('common.loading')}</div>;
  if (!article) return <div className="text-center py-12">Articulo no encontrado</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in" data-testid="article-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>

      <article className="bg-white rounded-2xl shadow-sm overflow-hidden p-8">
        <h1 className="font-serif text-4xl font-bold text-primary mb-4">{getLocalizedText(article.title, i18n.language)}</h1>
        <div className="flex items-center text-secondary mb-8">
          <Calendar className="h-4 w-4 mr-2" />
          {formatDate(article.created_at)}
        </div>
        <div className="prose max-w-none">
          <p className="text-lg text-secondary leading-relaxed whitespace-pre-line">{getLocalizedText(article.content, i18n.language)}</p>
        </div>
        {article.tags?.length > 0 && (
          <div className="mt-8 pt-6 border-t">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, i) => (
                <Badge key={i} variant="outline">{tag}</Badge>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
